/**
 * Checkout 主题配置（与 NewCheckout `templateConfig.checkout.theme` 数据结构一致）。
 * 编辑器只产出主题配置对象，真正的样式应用在远程 checkout 页面完成。
 */
export type CheckoutTemplateConfig = {
  bgColor?: string
  textColor?: string
  fontColor?: string
  fontSize?: number
  fontFamily?: string
  orderSummaryBgColor?: string
  orderSummaryTextColor?: string
  badgeBgColor?: string
  badgeTextColor?: string
  borderColor?: string
  interactiveColor?: string
  accentColor?: string
  buttonBgColor?: string
  buttonTextColor?: string
  selectedBgColor?: string
  selectedBorderColor?: string
  selectedTitleTextColor?: string
  selectedRadius?: number
  errorColor?: string
  mutedTextColor?: string
  discountColor?: string
  /** Applied discount code chip background. */
  discountChipBgColor?: string
  /** Applied discount code chip text. */
  discountChipTextColor?: string
  /** Discount “Apply” button idle background. */
  discountApplyBgColor?: string
  /** Discount “Apply” button idle text. */
  discountApplyTextColor?: string
  /** Discount “Apply” button idle border. */
  discountApplyBorderColor?: string
  tooltipBgColor?: string
  tooltipTextColor?: string
  skeletonColor?: string
  shimmerBaseColor?: string
  shimmerHighlightColor?: string
  thumbBorderColor?: string
  thumbShadowColor1?: string
  thumbShadowColor2?: string
  thumbShadowColor3?: string
  inputBgColor?: string
  inputTextColor?: string
  inputBorderColor?: string
  inputBorderRadius?: number
}

const baseFont: Pick<CheckoutTemplateConfig, 'fontSize' | 'fontFamily'> = {
  fontSize: 10,
  fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
}

/** 预设主题（写死，不可编辑）。与 NewCheckout checkoutThemePresets 保持一致。 */
export const checkoutThemePresets = {
  defaultLight: {
    ...baseFont,
    bgColor: '#ffffff',
    textColor: '#6b6375',
    fontColor: '#000000',
    orderSummaryBgColor: '#f5f5f5',
    borderColor: '#e5e4e7',
    accentColor: '#005bd1',
    interactiveColor: '#005bd1',
    buttonBgColor: '#005bd1',
    buttonTextColor: '#ffffff',
    selectedBgColor: 'rgb(243, 245, 255)',
    selectedBorderColor: '#005bd1',
    selectedTitleTextColor: '#000000',
    selectedRadius: 12,
    errorColor: '#dc2626',
    mutedTextColor: '#6b6375',
    discountColor: '#666666',
    discountChipBgColor: '#e5e4e7',
    discountChipTextColor: '#000000',
    discountApplyBgColor: '#e5e4e7',
    discountApplyTextColor: '#6b6375',
    discountApplyBorderColor: '#e5e4e7',
    tooltipBgColor: 'rgba(0, 0, 0, 0.88)',
    tooltipTextColor: '#ffffff',
    skeletonColor: 'rgba(0, 0, 0, 0.085)',
    shimmerBaseColor: 'rgba(0, 0, 0, 0.06)',
    shimmerHighlightColor: 'rgba(255, 255, 255, 0.55)',
    thumbBorderColor: '#ffffff',
    thumbShadowColor1: 'rgba(0, 0, 0, 0.12)',
    thumbShadowColor2: 'rgba(0, 0, 0, 0.09)',
    thumbShadowColor3: 'rgba(0, 0, 0, 0.03)',
    badgeBgColor: '#000000',
    badgeTextColor: '#ffffff',
    inputBgColor: '#ffffff',
    inputTextColor: '#000000',
    inputBorderColor: '#e5e4e7',
    inputBorderRadius: 12,
  },
  pink: {
    ...baseFont,
    bgColor: '#ffffff',
    textColor: '#7f1d1d',
    fontColor: '#7f1d1d',
    orderSummaryBgColor: '#fff1f7',
    borderColor: '#f0abcf',
    accentColor: '#db2777',
    interactiveColor: '#db2777',
    buttonBgColor: '#be185d',
    buttonTextColor: '#ffffff',
    selectedBgColor: '#ffe4f1',
    selectedBorderColor: '#db2777',
    selectedTitleTextColor: '#7f1d1d',
    selectedRadius: 12,
    errorColor: '#be123c',
    mutedTextColor: '#9f1239',
    discountColor: '#be185d',
    discountChipBgColor: '#fce7f3',
    discountChipTextColor: '#7f1d1d',
    discountApplyBgColor: '#fce7f3',
    discountApplyTextColor: '#9f1239',
    discountApplyBorderColor: '#f0abcf',
    tooltipBgColor: '#3b0a24',
    tooltipTextColor: '#ffffff',
    skeletonColor: 'rgba(219, 39, 119, 0.14)',
    shimmerBaseColor: 'rgba(219, 39, 119, 0.10)',
    shimmerHighlightColor: 'rgba(255, 255, 255, 0.70)',
    thumbBorderColor: '#ffd1e6',
    thumbShadowColor1: 'rgba(190, 24, 93, 0.18)',
    thumbShadowColor2: 'rgba(190, 24, 93, 0.12)',
    thumbShadowColor3: 'rgba(190, 24, 93, 0.06)',
    badgeBgColor: '#be185d',
    badgeTextColor: '#ffffff',
    inputBgColor: '#fffdf5',
    inputTextColor: '#db2777',
    inputBorderColor: '#f0abcf',
    inputBorderRadius: 12,
  },
  yellowBrown: {
    ...baseFont,
    bgColor: '#fffdf5',
    textColor: '#5b4a00',
    fontColor: '#2b2200',
    orderSummaryBgColor: '#f3e7d7',
    borderColor: '#fbbf24',
    accentColor: '#f59e0b',
    interactiveColor: '#f59e0b',
    buttonBgColor: '#f59e0b',
    buttonTextColor: '#111827',
    selectedBgColor: '#f3e7d7',
    selectedBorderColor: '#f59e0b',
    selectedTitleTextColor: '#2b2200',
    selectedRadius: 6,
    errorColor: '#be123c',
    mutedTextColor: '#7c6f2a',
    discountColor: '#b45309',
    discountChipBgColor: '#fde68a',
    discountChipTextColor: '#2b2200',
    discountApplyBgColor: '#fde68a',
    discountApplyTextColor: '#7c6f2a',
    discountApplyBorderColor: '#fbbf24',
    tooltipBgColor: '#2b2200',
    tooltipTextColor: '#ffffff',
    skeletonColor: 'rgba(245, 158, 11, 0.16)',
    shimmerBaseColor: 'rgba(245, 158, 11, 0.10)',
    shimmerHighlightColor: 'rgba(255, 255, 255, 0.70)',
    thumbBorderColor: '#fde68a',
    thumbShadowColor1: 'rgba(180, 83, 9, 0.18)',
    thumbShadowColor2: 'rgba(180, 83, 9, 0.12)',
    thumbShadowColor3: 'rgba(180, 83, 9, 0.06)',
    badgeBgColor: '#f59e0b',
    badgeTextColor: '#111827',
    inputBgColor: '#fffdf5',
    inputTextColor: '#5b4a00',
    inputBorderColor: '#fbbf24',
    inputBorderRadius: 6,
  },
  highContrast: {
    ...baseFont,
    bgColor: '#ffffff',
    textColor: '#111827',
    fontColor: '#000000',
    orderSummaryBgColor: '#f3f4f6',
    borderColor: '#111827',
    accentColor: '#111827',
    interactiveColor: '#111827',
    buttonBgColor: '#111827',
    buttonTextColor: '#ffffff',
    selectedBgColor: '#f3f4f6',
    selectedBorderColor: '#111827',
    selectedTitleTextColor: '#000000',
    selectedRadius: 6,
    errorColor: '#dc2626',
    mutedTextColor: '#6b7280',
    discountColor: '#111827',
    discountChipBgColor: '#e5e7eb',
    discountChipTextColor: '#000000',
    discountApplyBgColor: '#e5e7eb',
    discountApplyTextColor: '#111827',
    discountApplyBorderColor: '#111827',
    tooltipBgColor: '#111827',
    tooltipTextColor: '#ffffff',
    skeletonColor: 'rgba(17, 24, 39, 0.10)',
    shimmerBaseColor: 'rgba(17, 24, 39, 0.06)',
    shimmerHighlightColor: 'rgba(255, 255, 255, 0.65)',
    thumbBorderColor: '#ffffff',
    thumbShadowColor1: 'rgba(0, 0, 0, 0.18)',
    thumbShadowColor2: 'rgba(0, 0, 0, 0.12)',
    thumbShadowColor3: 'rgba(0, 0, 0, 0.06)',
    badgeBgColor: '#111827',
    badgeTextColor: '#ffffff',
    inputBgColor: '#ffffff',
    inputTextColor: '#000000',
    inputBorderColor: '#111827',
    inputBorderRadius: 6,
  },
  dark: {
    ...baseFont,
    bgColor: '#0b0f19',
    textColor: '#cbd5e1',
    fontColor: '#f8fafc',
    orderSummaryBgColor: '#111827',
    borderColor: '#334155',
    accentColor: '#f59e0b',
    interactiveColor: '#f59e0b',
    buttonBgColor: '#f59e0b',
    buttonTextColor: '#111827',
    selectedBgColor: 'rgba(245, 158, 11, 0.12)',
    selectedBorderColor: '#f59e0b',
    selectedTitleTextColor: '#f8fafc',
    selectedRadius: 12,
    errorColor: '#fb7185',
    mutedTextColor: '#94a3b8',
    discountColor: '#fbbf24',
    discountChipBgColor: 'rgba(255, 255, 255, 0.1)',
    discountChipTextColor: '#f8fafc',
    discountApplyBgColor: 'rgba(255, 255, 255, 0.1)',
    discountApplyTextColor: '#cbd5e1',
    discountApplyBorderColor: '#334155',
    tooltipBgColor: '#0b0f19',
    tooltipTextColor: '#f8fafc',
    skeletonColor: 'rgba(203, 213, 225, 0.10)',
    shimmerBaseColor: 'rgba(203, 213, 225, 0.08)',
    shimmerHighlightColor: 'rgba(255, 255, 255, 0.16)',
    thumbBorderColor: 'rgba(255, 255, 255, 0.14)',
    thumbShadowColor1: 'rgba(0, 0, 0, 0.30)',
    thumbShadowColor2: 'rgba(0, 0, 0, 0.22)',
    thumbShadowColor3: 'rgba(0, 0, 0, 0.14)',
    badgeBgColor: '#f59e0b',
    badgeTextColor: '#111827',
    inputBgColor: '#0b0f19',
    inputTextColor: '#f8fafc',
    inputBorderColor: '#334155',
    inputBorderRadius: 12,
  },
} satisfies Record<string, CheckoutTemplateConfig>

export type CheckoutThemePresetName = keyof typeof checkoutThemePresets
export type CheckoutThemePresetId = CheckoutThemePresetName | 'custom'

export type ThemePresetMeta = {
  id: CheckoutThemePresetName
  label: string
}

/** 预设展示顺序与中文名 */
export const THEME_PRESET_LIST: ThemePresetMeta[] = [
  { id: 'defaultLight', label: 'Default Light' },
  { id: 'pink', label: 'Pink' },
  { id: 'yellowBrown', label: 'Yellow Brown' },
  { id: 'highContrast', label: 'High Contrast' },
  { id: 'dark', label: 'Dark' },
]

export type ThemeFieldType = 'color' | 'number' | 'font'

export type ThemeField = {
  key: keyof CheckoutTemplateConfig
  label: string
  type: ThemeFieldType
}

export type ThemeFieldGroup = {
  title: string
  fields: ThemeField[]
}

/** 自定义主题可编辑字段（分组展示）。 */
export const THEME_FIELD_GROUPS: ThemeFieldGroup[] = [
  {
    title: 'Basic',
    fields: [
      { key: 'bgColor', label: 'Page Background', type: 'color' },
      { key: 'textColor', label: 'Body Text', type: 'color' },
      { key: 'fontColor', label: 'Heading Text', type: 'color' },
      { key: 'orderSummaryBgColor', label: 'Order Summary Background', type: 'color' },
      { key: 'borderColor', label: 'Border Color', type: 'color' },
      { key: 'fontFamily', label: 'Font Family', type: 'font' },
      { key: 'fontSize', label: 'Base Font Size (px)', type: 'number' },
    ],
  },
  {
    title: 'Primary Colors',
    fields: [
      { key: 'accentColor', label: 'Accent Color', type: 'color' },
      { key: 'interactiveColor', label: 'Interactive Color', type: 'color' },
    ],
  },
  {
    title: 'Button',
    fields: [
      { key: 'buttonBgColor', label: 'Button Background', type: 'color' },
      { key: 'buttonTextColor', label: 'Button Text', type: 'color' },
    ],
  },
  {
    title: 'Selected Item',
    fields: [
      { key: 'selectedBgColor', label: 'Selected Background', type: 'color' },
      { key: 'selectedBorderColor', label: 'Selected Border', type: 'color' },
      { key: 'selectedTitleTextColor', label: 'Selected Title', type: 'color' },
      { key: 'selectedRadius', label: 'Selected Radius (px)', type: 'number' },
    ],
  },
  {
    title: 'Input',
    fields: [
      { key: 'inputBgColor', label: 'Input Background', type: 'color' },
      { key: 'inputTextColor', label: 'Input Text', type: 'color' },
      { key: 'inputBorderColor', label: 'Input Border', type: 'color' },
      { key: 'inputBorderRadius', label: 'Input Radius (px)', type: 'number' },
    ],
  },
  {
    title: 'Discount Code',
    fields: [
      { key: 'discountColor', label: 'Discount Color', type: 'color' },
      { key: 'discountChipBgColor', label: 'Chip Background', type: 'color' },
      { key: 'discountChipTextColor', label: 'Chip Text', type: 'color' },
      { key: 'discountApplyBgColor', label: 'Apply Background', type: 'color' },
      { key: 'discountApplyTextColor', label: 'Apply Text', type: 'color' },
      { key: 'discountApplyBorderColor', label: 'Apply Border', type: 'color' },
    ],
  },
  {
    title: 'Other',
    fields: [
      { key: 'errorColor', label: 'Error Color', type: 'color' },
      { key: 'mutedTextColor', label: 'Muted Text', type: 'color' },
      { key: 'badgeBgColor', label: 'Badge Background', type: 'color' },
      { key: 'badgeTextColor', label: 'Badge Text', type: 'color' },
    ],
  },
]

/** 主题字段中属于 number 类型的 key（用于编辑器做数值转换）。 */
export const THEME_NUMBER_KEYS: (keyof CheckoutTemplateConfig)[] = [
  'fontSize',
  'selectedRadius',
  'inputBorderRadius',
]

export function cloneThemeConfig(theme: CheckoutTemplateConfig): CheckoutTemplateConfig {
  return { ...theme }
}

/** 取预设主题副本（避免直接引用常量被修改）。 */
export function getPresetTheme(id: CheckoutThemePresetName): CheckoutTemplateConfig {
  return { ...checkoutThemePresets[id] }
}

/** 从已保存的对象里挑出已知的主题字段，忽略多余字段。 */
export function sanitizeThemeConfig(raw: unknown): CheckoutTemplateConfig {
  const out: CheckoutTemplateConfig = {}
  if (!raw || typeof raw !== 'object') return out
  const src = raw as Record<string, unknown>
  const numberKeys = new Set<string>(THEME_NUMBER_KEYS as string[])
  for (const group of THEME_FIELD_GROUPS) {
    for (const field of group.fields) {
      const v = src[field.key]
      if (v == null) continue
      if (numberKeys.has(field.key)) {
        const n = Number(v)
        if (Number.isFinite(n)) out[field.key] = n as never
      } else if (typeof v === 'string') {
        out[field.key] = v as never
      }
    }
  }
  return out
}
